import type { AppEvent } from './events.js';

/**
 * PROGRESSION RULES — as data, not code.
 *
 * Every XP value, rank threshold, title condition and achievement lives in
 * one versioned object. Three reasons that matters more than it looks:
 *
 *  • You *will* rebalance XP. Nobody gets it right first time. A version bump
 *    plus a full replay recomputes every user correctly — impossible if the
 *    maths were sprinkled through screens or baked into stored counters.
 *  • Titles need a rolling window and a scoring pass, or they never change
 *    and "what kind of student am I?" quietly dies.
 *  • A white-label partner can rename ranks ("Udvash Scholar") or retune
 *    thresholds without touching progression logic.
 */

export type RankId =
  | 'novice'
  | 'aspirant'
  | 'scholar'
  | 'elite'
  | 'master'
  | 'grandmaster'
  | 'legend'
  | 'nationalTopper';

export type TitleId =
  | 'nightOwl'
  | 'earlyBird'
  | 'ironWill'
  | 'unbreakable'
  | 'bookworm'
  | 'revisionMaster'
  | 'focusedMind'
  | 'pomodoroPro'
  | 'marathonStudier'
  | 'phoenix';

/**
 * Behaviour over a rolling window. Titles are scored against this rather than
 * lifetime totals — a student who was a Night Owl in June should not still be
 * wearing it in December.
 */
export type BehaviourWindow = {
  windowDays: number;
  activeDays: number;
  currentStreak: number;
  habitsCompleted: number;
  habitsExpected: number;
  tasksCompleted: number;
  tasksOpened: number;
  minutes: number;
  reviewMinutes: number;
  timedSessions: number;
  pomodoroSessions: number;
  /** Sessions of 90 minutes or more. */
  longSessions: number;
  /** Activity logged 22:00–03:59 local. */
  lateActivities: number;
  /** Activity logged 04:00–08:00 local. */
  earlyActivities: number;
  activities: number;
  /** Length of the inactive gap immediately before the current streak. */
  gapBeforeStreak: number;
};

export type AchievementContext = {
  totalMinutes: number;
  totalTimedSessions: number;
  currentStreak: number;
  longestStreak: number;
  goalsStarted: number;
  goalsCompleted: number;
  window: BehaviourWindow;
};

export type RankTier = { id: RankId; minPower: number };

export type TitleRule = {
  id: TitleId;
  /** Higher wins when more titles qualify than `maxTitles` allows. */
  priority: number;
  test: (w: BehaviourWindow) => boolean;
};

export type AchievementRule = {
  id: string;
  test: (c: AchievementContext) => boolean;
};

export type Ruleset = {
  version: number;
  xp: {
    /** Flat awards for discrete completions. */
    flat: Partial<Record<AppEvent['type'], number>>;
    /**
     * Extra for an entry that came from a running timer.
     *
     * Cannot key off the event type any more, because a typed hour and a timed
     * hour are now the same event. It is `mode` that earns this, and what it
     * pays for is committing to a bounded block in advance rather than
     * reporting on one afterwards.
     */
    timedSessionBonus: number;
    /** Diminishing-returns curve for logged minutes. */
    minutes: (minutes: number) => number;
    /** Ceiling on minute-derived XP per study day. */
    dailyMinutesCap: number;
    /** Awarded once per active day, scaled by streak length. */
    streakBonus: (streakDays: number) => number;
    /**
     * What a floor completion earns, as a fraction of the full award.
     *
     * Deliberately generous rather than token. The floor has to be worth
     * tapping on the day the student is exhausted, because that is the only
     * day it exists for — price it as a consolation and it will be skipped
     * exactly when it matters, which returns us to the ruined day it was
     * added to prevent.
     */
    floorRatio: number;
    /**
     * Awarded to the first active day after a gap (§48 #3).
     *
     * Larger than any single day's streak bonus, on purpose. Returning after a
     * miss is the highest-value behaviour in the literature — the top-ranked
     * intervention of 54 in a 61,293-person trial — so it must out-earn simply
     * having never stopped. This is the one place the scoring says something
     * the user could not have guessed, and it is the point of the product.
     */
    comebackBonus: number;
  };
  /**
   * Spaced retrieval (§43, d = 0.54) — as data, because the evidence supports
   * SPACING and does not bless any particular sequence. Rebalancing the ladder
   * is a version bump and a replay, not a code change.
   */
  review: {
    /**
     * Days until the next review, indexed by how many reviews a topic has
     * already had. The last rung repeats forever — a topic reviewed six times
     * does not stop needing revisiting, it just needs it rarely.
     */
    ladder: number[];
    /** Rough budget per topic, for the "about 12 min" on the card. */
    minutesPerItem: number;
    /**
     * Ceilings on one day's proposal.
     *
     * A student returning after two weeks has thirty due topics, and showing
     * all thirty is a wall of debt at exactly the moment the Comeback card is
     * trying to welcome them back. The rest stay silent rather than becoming
     * a backlog to feel bad about.
     */
    maxItems: number;
    maxMinutes: number;
  };
  ranks: RankTier[];
  titles: TitleRule[];
  maxTitles: number;
  achievements: AchievementRule[];
  /**
   * How many days back a completion may be recorded.
   *
   * The week strip invites ticking past cells, and ticking past cells is
   * streak fraud made trivial. "I forgot yesterday" is legitimate; "let me
   * fill in last month" is not. 2 days covers real forgetting.
   */
  backfillWindowDays: number;
};

/**
 * Spec §7: "Avoid making raw hours the only source of XP."
 *
 * Tiered so the first hour is worth the most and a claimed 8-hour session is
 * worth far less per minute than the first 60. Combined with the daily cap
 * this makes inflating manual logs mostly pointless — which matters, because
 * manual entry is unverifiable by design (§17).
 */
function minutesToXp(minutes: number): number {
  const capped = Math.max(0, Math.min(minutes, 600));
  const first = Math.min(capped, 60);
  const second = Math.min(Math.max(capped - 60, 0), 60);
  const rest = Math.max(capped - 120, 0);
  return Math.round(first * 1 + second * 0.6 + rest * 0.3);
}

export const defaultRuleset: Ruleset = {
  version: 2,
  backfillWindowDays: 2,

  xp: {
    flat: {
      'habit.completed': 10,
      'task.completed': 15,
      'goal.partCompleted': 60,
      'reading.recorded': 25,
    },
    // Finishing a *planned* session is rewarded separately from its length, so
    // consistency beats marathon sessions.
    timedSessionBonus: 20,
    minutes: minutesToXp,
    dailyMinutesCap: 250,
    // Caps at 10 days: streaks should reward returning, not compound forever.
    streakBonus: (days) => Math.min(days, 10) * 5,
    floorRatio: 0.3,
    // 60 against a maximum streak bonus of 50: a comeback day is worth more
    // than the best ordinary day, and visibly so.
    comebackBonus: 60,
  },

  review: {
    // Expanding, and deliberately gentle at the top: a sixth review 35 days
    // out is a reminder that the material exists, which is all it needs to be.
    ladder: [1, 3, 7, 16, 35],
    minutesPerItem: 4,
    maxItems: 4,
    maxMinutes: 20,
  },

  // Roughly: Scholar in a couple of months of real use, National Topper as a
  // genuine multi-year marker. Tuned by feel — expect to rebalance once real
  // logs exist, which is exactly what `version` is for.
  ranks: [
    { id: 'novice', minPower: 0 },
    { id: 'aspirant', minPower: 500 },
    { id: 'scholar', minPower: 2_500 },
    { id: 'elite', minPower: 8_000 },
    { id: 'master', minPower: 20_000 },
    { id: 'grandmaster', minPower: 45_000 },
    { id: 'legend', minPower: 90_000 },
    { id: 'nationalTopper', minPower: 175_000 },
  ],

  maxTitles: 2,
  titles: [
    // Every rule carries a volume floor as well as a ratio. Without it, one
    // late-night session on a quiet week crowns someone Night Owl.
    {
      id: 'phoenix',
      priority: 100,
      test: (w) => w.gapBeforeStreak >= 7 && w.currentStreak >= 5,
    },
    {
      id: 'unbreakable',
      priority: 90,
      test: (w) => w.currentStreak >= 21,
    },
    {
      id: 'ironWill',
      priority: 80,
      test: (w) =>
        w.activeDays >= 10 && w.habitsExpected >= 20 && w.habitsCompleted / w.habitsExpected >= 0.85,
    },
    {
      id: 'marathonStudier',
      priority: 70,
      test: (w) => w.longSessions >= 5,
    },
    {
      id: 'pomodoroPro',
      priority: 65,
      test: (w) => w.pomodoroSessions >= 12,
    },
    {
      id: 'revisionMaster',
      priority: 60,
      test: (w) => w.minutes >= 300 && w.reviewMinutes / w.minutes >= 0.3,
    },
    {
      id: 'bookworm',
      priority: 55,
      test: (w) => w.minutes >= 900,
    },
    {
      id: 'focusedMind',
      priority: 50,
      test: (w) => w.tasksOpened >= 8 && w.tasksCompleted / w.tasksOpened >= 0.8,
    },
    {
      id: 'nightOwl',
      priority: 40,
      test: (w) => w.activities >= 8 && w.lateActivities / w.activities >= 0.4,
    },
    {
      id: 'earlyBird',
      priority: 40,
      test: (w) => w.activities >= 8 && w.earlyActivities / w.activities >= 0.4,
    },
  ],

  achievements: [
    { id: 'firstStudySession', test: (c) => c.totalTimedSessions >= 1 || c.totalMinutes > 0 },
    { id: 'streak7', test: (c) => c.longestStreak >= 7 },
    { id: 'streak30', test: (c) => c.longestStreak >= 30 },
    { id: 'streak100', test: (c) => c.longestStreak >= 100 },
    { id: 'hours100', test: (c) => c.totalMinutes >= 6_000 },
    { id: 'hours500', test: (c) => c.totalMinutes >= 30_000 },
    { id: 'revisionMaster', test: (c) => c.window.reviewMinutes >= 600 },
    { id: 'goalStarted', test: (c) => c.goalsStarted >= 1 },
    { id: 'goalCompleted', test: (c) => c.goalsCompleted >= 1 },
    { id: 'earlyBird', test: (c) => c.window.earlyActivities >= 15 },
    { id: 'nightOwl', test: (c) => c.window.lateActivities >= 15 },
  ],
};

export function rankFor(power: number, ruleset: Ruleset): RankTier {
  let current = ruleset.ranks[0] as RankTier;
  for (const tier of ruleset.ranks) if (power >= tier.minPower) current = tier;
  return current;
}

/** Next tier and progress toward it — drives the Profile progress bar. */
export function rankProgress(
  power: number,
  ruleset: Ruleset,
): { current: RankTier; next: RankTier | null; ratio: number } {
  const current = rankFor(power, ruleset);
  const index = ruleset.ranks.findIndex((t) => t.id === current.id);
  const next = ruleset.ranks[index + 1] ?? null;
  if (!next) return { current, next: null, ratio: 1 };
  const span = next.minPower - current.minPower;
  return { current, next, ratio: span <= 0 ? 1 : (power - current.minPower) / span };
}
